import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ClusterResponse,
  MultiSearchResponse,
  PerWordResult,
  VerseMeta,
  WorkerResponse,
} from "../core/types";

export interface MultiSearchParams {
  words: string[];
  dMin: number;
  dMax: number;
  backward: boolean;
  maxResultsPerWord?: number;
}

export interface ClusterParams {
  hit: { p: number; d: number; len: number };
  rowsAbove: number;
  rowsBelow: number;
  words: string[];
}

export interface UseSearchWorker {
  ready: boolean;
  N: number;
  verses: VerseMeta[];
  searchResults: PerWordResult[];
  searchElapsedMs: number;
  clusterResult: ClusterResponse | null;
  searching: boolean;
  error: string | null;
  multiSearch: (params: MultiSearchParams) => void;
  cluster: (params: ClusterParams) => void;
}

// One frame of debounce. The worker is fast enough that more than this is
// just added latency.
const SEARCH_DEBOUNCE_MS = 16;

const EMPTY_RESULTS: PerWordResult[] = [];

export function useSearchWorker(): UseSearchWorker {
  const workerRef = useRef<Worker | null>(null);
  // All mutating refs live outside React state so they don't cause
  // re-renders or invalidate effect dependencies.
  const reqIdRef = useRef(0);
  const searchIdRef = useRef(0);
  const clusterIdRef = useRef(0);
  const searchTimerRef = useRef<number | null>(null);

  const [ready, setReady] = useState(false);
  const [N, setN] = useState(0);
  const [verses, setVerses] = useState<VerseMeta[]>([]);
  const [searchResults, setSearchResults] = useState<PerWordResult[]>(EMPTY_RESULTS);
  const [searchElapsedMs, setSearchElapsedMs] = useState(0);
  const [clusterResult, setClusterResult] = useState<ClusterResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const worker = new Worker(
      new URL("../workers/search.worker.ts", import.meta.url),
      { type: "module" },
    );
    workerRef.current = worker;

    worker.addEventListener("message", (ev: MessageEvent<WorkerResponse>) => {
      const msg = ev.data;
      if (msg.type === "ready") {
        setReady(true);
        setN(msg.N);
        setVerses(msg.verses);
      } else if (msg.type === "multiSearch") {
        const ms = msg as MultiSearchResponse;
        if (ms.id === searchIdRef.current) {
          setSearchResults(ms.results.length === 0 ? EMPTY_RESULTS : ms.results);
          setSearchElapsedMs(ms.totalElapsedMs);
          setSearching(false);
        }
      } else if (msg.type === "cluster") {
        if (msg.id === clusterIdRef.current) {
          setClusterResult(msg);
        }
      } else if (msg.type === "error") {
        setError(msg.message);
        setSearching(false);
      }
    });

    return () => {
      worker.terminate();
      workerRef.current = null;
      if (searchTimerRef.current !== null) {
        window.clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
    };
    // The worker lifecycle is mount/unmount only; setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const multiSearch = useCallback((params: MultiSearchParams) => {
    if (searchTimerRef.current !== null) {
      window.clearTimeout(searchTimerRef.current);
    }
    setSearching(true);
    searchTimerRef.current = window.setTimeout(() => {
      const w = workerRef.current;
      if (!w) return;
      const id = ++reqIdRef.current;
      searchIdRef.current = id;
      w.postMessage({
        type: "multiSearch",
        id,
        words: params.words,
        dMin: params.dMin,
        dMax: params.dMax,
        backward: params.backward,
        maxResultsPerWord: params.maxResultsPerWord,
      });
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const cluster = useCallback((params: ClusterParams) => {
    const w = workerRef.current;
    if (!w) return;
    const id = ++reqIdRef.current;
    clusterIdRef.current = id;
    w.postMessage({
      type: "cluster",
      id,
      hit: params.hit,
      rowsAbove: params.rowsAbove,
      rowsBelow: params.rowsBelow,
      words: params.words,
    });
  }, []);

  return {
    ready,
    N,
    verses,
    searchResults,
    searchElapsedMs,
    clusterResult,
    searching,
    error,
    multiSearch,
    cluster,
  };
}
